/**
 * @page /qm/test/:token (Magic-Link Test-Runner ohne Login)
 * @description 4-Phasen-Flow: Tour -> Test -> Submit -> Result mit Magic-Link-Token.
 * @api GET/POST /api/qm/magic/:token (kein Auth)
 * @since v2.406.0 (#919b)
 */
import { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowRight, Award, CheckCircle2, AlertTriangle, Loader2, Trophy, XCircle } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

export default function QmTestMagicPage() {
  const { token } = useParams();

  const [phase, setPhase] = useState('intro');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tourIdx, setTourIdx] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [versuch, setVersuch] = useState(null);
  const [answers, setAnswers] = useState({});
  const [frageIdx, setFrageIdx] = useState(0);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/qm/magic/${token}`)
      .then(async r => {
        if (r.status === 410) { setError('expired'); setLoading(false); return null; }
        if (!r.ok) { setError('not_found'); setLoading(false); return null; }
        return r.json();
      })
      .then(d => {
        if (!d) return;
        setData(d);
        setLoading(false);
        if (d.status === 'bestanden') {
          setResult({
            bestanden: true,
            score_pct: d.versuche?.[0]?.score_pct || 100,
            bestehensgrenze_pct: d.bestehensgrenze_pct,
            isFromHistory: true,
          });
          setPhase('result');
        } else {
          const arr = Array.isArray(d.tour_bestaetigte_schritte) ? d.tour_bestaetigte_schritte : [];
          setTourIdx(arr.length);
        }
      })
      .catch(() => { setError('not_found'); setLoading(false); });
  }, [token]);

  const schritte = data?.schritte || [];
  const bestaetigtArr = useMemo(() => Array.isArray(data?.tour_bestaetigte_schritte) ? data.tour_bestaetigte_schritte : [], [data]);
  const tourComplete = schritte.length > 0 && schritte.every(s => bestaetigtArr.includes(s.id));

  const confirmStep = async () => {
    const s = schritte[tourIdx];
    if (!s) return;
    setConfirming(true);
    try {
      const res = await fetch(`${API}/api/qm/magic/${token}/tour-step`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ schritt_id: s.id }),
      });
      if (!res.ok) throw new Error();
      const r = await res.json();
      setData(prev => ({ ...prev, tour_bestaetigte_schritte: r.bestaetigt }));
      if (tourIdx < schritte.length - 1) setTourIdx(tourIdx + 1);
    } finally {
      setConfirming(false);
    }
  };

  const startTest = async () => {
    setStarting(true); setError(null);
    try {
      const res = await fetch(`${API}/api/qm/magic/${token}/start`, { method: 'POST' });
      const r = await res.json();
      if (!res.ok) { setError(r.error || 'Test konnte nicht gestartet werden'); setStarting(false); return; }
      setVersuch(r); setAnswers({}); setFrageIdx(0); setPhase('test');
    } catch { setError('Netzwerkfehler'); }
    finally { setStarting(false); }
  };

  const toggleAnswer = (frageId, antwortId, isMulti) => {
    setAnswers(prev => {
      const cur = prev[frageId] || [];
      if (isMulti) {
        return { ...prev, [frageId]: cur.includes(antwortId) ? cur.filter(x => x !== antwortId) : [...cur, antwortId] };
      }
      return { ...prev, [frageId]: [antwortId] };
    });
  };

  const submitTest = async () => {
    setSubmitting(true); setError(null);
    try {
      const res = await fetch(`${API}/api/qm/magic/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ versuch_id: versuch.versuch_id, antworten: answers }),
      });
      const r = await res.json();
      if (!res.ok) { setError(r.error || 'Submit fehlgeschlagen'); setSubmitting(false); return; }
      setResult({ ...r, bestehensgrenze_pct: versuch.bestehensgrenze_pct });
      setPhase('result');
    } catch { setError('Netzwerkfehler'); }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8F9FC' }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#338edc' }} />
      </div>
    );
  }
  if (error === 'expired') {
    return (
      <div className="min-h-screen p-6 flex flex-col items-center justify-center" style={{ background: '#F8F9FC' }}>
        <AlertTriangle className="w-10 h-10 mb-3" style={{ color: '#F59E0B' }} />
        <p className="text-sm" style={{ color: '#6B7280' }}>Link abgelaufen. Bitte fordere einen neuen Link an.</p>
      </div>
    );
  }
  if (error === 'not_found' || !data) {
    return (
      <div className="min-h-screen p-6 flex flex-col items-center justify-center" style={{ background: '#F8F9FC' }}>
        <AlertTriangle className="w-10 h-10 mb-3" style={{ color: '#F59E0B' }} />
        <p className="text-sm" style={{ color: '#6B7280' }}>Schulung nicht gefunden.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F8F9FC' }}>
      <div className="px-4 py-4 border-b sticky top-0 z-10" style={{ borderColor: '#E5E7EB', background: '#FFFFFF' }}>
        <p className="text-xs truncate" style={{ color: '#6B7280' }}>{data.prozess_titel}</p>
        <h1 className="text-sm font-semibold truncate" style={{ color: '#1A1A2E' }}>{data.test_titel}</h1>
        <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>fuer {data.vorname} {data.nachname}</p>
      </div>

      <div className="p-4 max-w-2xl mx-auto pb-24">

        {phase === 'intro' && (
          <div className="space-y-4">
            <div className="rounded-2xl p-5" style={{ background: '#FFFFFF', border: '1px solid #E5E7EB' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: '#EFF6FF' }}>
                  <Award className="w-6 h-6" strokeWidth={1.5} style={{ color: '#338edc' }} />
                </div>
                <div>
                  <h2 className="text-base font-semibold" style={{ color: '#1A1A2E' }}>{data.test_titel}</h2>
                  <p className="text-xs" style={{ color: '#6B7280' }}>{data.prozess_titel}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg p-3" style={{ background: '#F8F9FC' }}>
                  <p style={{ color: '#6B7280' }}>Bestehensgrenze</p>
                  <p className="text-sm font-semibold mt-0.5" style={{ color: '#1A1A2E' }}>{data.bestehensgrenze_pct}%</p>
                </div>
                <div className="rounded-lg p-3" style={{ background: '#F8F9FC' }}>
                  <p style={{ color: '#6B7280' }}>Fragen</p>
                  <p className="text-sm font-semibold mt-0.5" style={{ color: '#1A1A2E' }}>{data.anzahl_fragen_aus_pool || 'alle'}</p>
                </div>
                <div className="rounded-lg p-3" style={{ background: '#F8F9FC' }}>
                  <p style={{ color: '#6B7280' }}>Max. Versuche</p>
                  <p className="text-sm font-semibold mt-0.5" style={{ color: '#1A1A2E' }}>{data.max_versuche}</p>
                </div>
                <div className="rounded-lg p-3" style={{ background: '#F8F9FC' }}>
                  <p style={{ color: '#6B7280' }}>Cooldown</p>
                  <p className="text-sm font-semibold mt-0.5" style={{ color: '#1A1A2E' }}>{data.wiederholung_cooldown_h}h</p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl p-4" style={{ background: '#EFF6FF', border: '1px solid #338edc30' }}>
              <p className="text-sm font-medium mb-1" style={{ color: '#1e40af' }}>Schulungs-Tour vor dem Test</p>
              <p className="text-xs" style={{ color: '#1e40af' }}>Du gehst zuerst alle Schritte des Prozesses durch. Erst danach kannst du den Test starten.</p>
            </div>
            <button
              onClick={() => setPhase('tour')}
              className="w-full py-3 rounded-2xl text-sm font-medium flex items-center justify-center gap-2"
              style={{ background: '#338edc', color: '#fff' }}
            >
              {tourComplete ? 'Tour ansehen' : 'Tour starten'}
              <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </button>
            {tourComplete && (
              <button
                onClick={startTest}
                disabled={starting}
                className="w-full py-3 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: '#10B981', color: '#fff' }}
              >
                {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
                Test starten
              </button>
            )}
            {error && (
              <div className="rounded-xl p-3 text-sm" style={{ background: '#FEF2F2', color: '#991B1B', border: '1px solid #FECACA' }}>{error}</div>
            )}
          </div>
        )}

        {phase === 'tour' && schritte.length > 0 && (() => {
          const step = schritte[tourIdx] || schritte[schritte.length - 1];
          const stepConfirmed = bestaetigtArr.includes(step.id);
          const isLast = tourIdx >= schritte.length - 1;
          return (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs" style={{ color: '#6B7280' }}>Schritt {tourIdx + 1} / {schritte.length}</p>
                <div className="flex gap-1">
                  {schritte.map((_, i) => (
                    <div key={i} className="h-1 w-6 rounded-full" style={{ background: i <= tourIdx ? '#338edc' : '#E5E7EB' }} />
                  ))}
                </div>
              </div>
              <div className="rounded-2xl p-5" style={{ background: '#FFFFFF', border: '1px solid #E5E7EB' }}>
                <h3 className="text-base font-semibold mb-2" style={{ color: '#1A1A2E' }}>{step.titel}</h3>
                {step.beschreibung && <p className="text-sm whitespace-pre-wrap" style={{ color: '#4B5563' }}>{step.beschreibung}</p>}
              </div>
              <div className="flex gap-2">
                {tourIdx > 0 && (
                  <button onClick={() => setTourIdx(tourIdx - 1)} className="flex-1 py-3 rounded-2xl text-sm font-medium border"
                    style={{ borderColor: '#E5E7EB', color: '#4B5563', background: '#FFFFFF' }}>Zurueck</button>
                )}
                {!stepConfirmed ? (
                  <button onClick={confirmStep} disabled={confirming}
                    className="flex-1 py-3 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ background: '#10B981', color: '#fff' }}>
                    {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Verstanden
                  </button>
                ) : !isLast ? (
                  <button onClick={() => setTourIdx(tourIdx + 1)}
                    className="flex-1 py-3 rounded-2xl text-sm font-medium flex items-center justify-center gap-2"
                    style={{ background: '#338edc', color: '#fff' }}>
                    Weiter <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button onClick={startTest} disabled={starting || !tourComplete}
                    className="flex-1 py-3 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ background: '#338edc', color: '#fff' }}>
                    {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
                    Test starten
                  </button>
                )}
              </div>
              {error && <div className="rounded-xl p-3 text-sm" style={{ background: '#FEF2F2', color: '#991B1B', border: '1px solid #FECACA' }}>{error}</div>}
            </div>
          );
        })()}

        {phase === 'test' && versuch && (() => {
          const frage = versuch.fragen[frageIdx];
          if (!frage) return null;
          const isMulti = frage.frage_typ === 'multi_choice';
          const userAnswers = answers[frage.id] || [];
          const isLast = frageIdx >= versuch.fragen.length - 1;
          const beantwortet = versuch.fragen.filter(f => (answers[f.id] || []).length > 0).length;
          return (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs" style={{ color: '#6B7280' }}>Frage {frageIdx + 1} / {versuch.fragen.length}</p>
                <p className="text-xs" style={{ color: '#6B7280' }}>{beantwortet}/{versuch.fragen.length} beantwortet</p>
              </div>
              <div className="h-1 w-full rounded-full overflow-hidden" style={{ background: '#E5E7EB' }}>
                <div className="h-full" style={{ width: `${((frageIdx + 1) / versuch.fragen.length) * 100}%`, background: '#338edc' }} />
              </div>
              <div className="rounded-2xl p-5" style={{ background: '#FFFFFF', border: '1px solid #E5E7EB' }}>
                <p className="text-xs mb-2" style={{ color: '#338edc' }}>{isMulti ? 'Mehrere Antworten moeglich' : 'Eine Antwort waehlen'}</p>
                <h3 className="text-base font-semibold mb-4" style={{ color: '#1A1A2E' }}>{frage.frage_text}</h3>
                <div className="space-y-2">
                  {frage.antworten.map(a => {
                    const sel = userAnswers.includes(a.id);
                    return (
                      <button key={a.id} onClick={() => toggleAnswer(frage.id, a.id, isMulti)}
                        className="w-full text-left p-3 rounded-xl border transition-all flex items-center gap-3"
                        style={{ borderColor: sel ? '#338edc' : '#E5E7EB', background: sel ? '#EFF6FF' : '#FFFFFF' }}>
                        <div className="w-5 h-5 shrink-0 flex items-center justify-center"
                          style={{ borderRadius: isMulti ? '4px' : '999px', border: `2px solid ${sel ? '#338edc' : '#D1D5DB'}`, background: sel ? '#338edc' : 'transparent' }}>
                          {sel && <CheckCircle2 className="w-3 h-3" style={{ color: '#fff' }} strokeWidth={3} />}
                        </div>
                        <span className="text-sm" style={{ color: '#1A1A2E' }}>{a.antwort_text}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex gap-2">
                {frageIdx > 0 && (
                  <button onClick={() => setFrageIdx(frageIdx - 1)} className="flex-1 py-3 rounded-2xl text-sm font-medium border"
                    style={{ borderColor: '#E5E7EB', color: '#4B5563', background: '#FFFFFF' }}>Zurueck</button>
                )}
                {!isLast ? (
                  <button onClick={() => setFrageIdx(frageIdx + 1)} disabled={userAnswers.length === 0}
                    className="flex-1 py-3 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ background: '#338edc', color: '#fff' }}>
                    Weiter <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button onClick={submitTest} disabled={submitting || beantwortet < versuch.fragen.length}
                    className="flex-1 py-3 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ background: '#10B981', color: '#fff' }}>
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Absenden
                  </button>
                )}
              </div>
              {error && <div className="rounded-xl p-3 text-sm" style={{ background: '#FEF2F2', color: '#991B1B', border: '1px solid #FECACA' }}>{error}</div>}
            </div>
          );
        })()}

        {phase === 'result' && result && (
          <div className="space-y-4">
            <div className="rounded-2xl p-6 text-center" style={{ background: result.bestanden ? '#ECFDF5' : '#FEF2F2', border: `1px solid ${result.bestanden ? '#A7F3D0' : '#FECACA'}` }}>
              <div className="w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ background: result.bestanden ? '#10B981' : '#EF4444' }}>
                {result.bestanden ? <Trophy className="w-8 h-8" style={{ color: '#fff' }} strokeWidth={1.5} />
                  : <XCircle className="w-8 h-8" style={{ color: '#fff' }} strokeWidth={1.5} />}
              </div>
              <h2 className="text-xl font-bold mb-1" style={{ color: result.bestanden ? '#065F46' : '#991B1B' }}>
                {result.bestanden ? 'Bestanden!' : 'Nicht bestanden'}
              </h2>
              <p className="text-3xl font-bold my-3" style={{ color: result.bestanden ? '#065F46' : '#991B1B' }}>{result.score_pct}%</p>
              <p className="text-xs" style={{ color: result.bestanden ? '#065F46' : '#991B1B' }}>
                Bestehensgrenze: {result.bestehensgrenze_pct}%
              </p>
              {result.richtig != null && (
                <p className="text-xs mt-2" style={{ color: result.bestanden ? '#065F46' : '#991B1B' }}>
                  {result.richtig} / {result.gesamt} Fragen richtig
                </p>
              )}
              {result.bestanden && result.zertifikat_id && (
                <a
                  href={`/api/zertifikate/${result.zertifikat_id}/pdf`}
                  target="_blank" rel="noopener noreferrer"
                  className="mt-4 inline-block py-2 px-4 rounded-lg text-xs font-medium"
                  style={{ background: '#338edc', color: '#fff' }}
                >
                  Zertifikat als PDF
                </a>
              )}
            </div>
            {!result.bestanden && (
              <button
                onClick={() => { setPhase('intro'); setResult(null); setError(null); }}
                className="w-full py-3 rounded-2xl text-sm font-medium border"
                style={{ borderColor: '#E5E7EB', color: '#4B5563', background: '#FFFFFF' }}
              >
                Erneut
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
